import {
  RECEIVE_FROM_API,
  RECEIVE_FROM_API_HOLDING_DATA,
  RECEIVE_INFOGRAPHICS_FROM_API,
  REQUEST_API,
  REQUEST_API_FAILURE,
  REQUEST_API_FAILURE_HOLDING_DATA,
  REQUEST_API_HOLDING_DATA,
  REQUEST_POST_ACR_INFO_BOX_SUCCESS,
} from 'src/constants';
import { REQUEST_API_LATEST, RESET_REPORTS_LIST } from 'src/redux/api/actions';
import type { BaseAction } from 'src/redux/api/types';
import { CLEAR_API_KEYS } from 'src/redux/api/types';
import {
  COMMENT_REPLY_REQUEST_API,
  COMMENT_REQUEST_API,
} from 'src/redux/comments/actions';
import * as comments from 'src/redux/comments/reducer';
import {
  ACR_INFO_BOX_ACTION,
  ACR_REPORT_STATE_TRANSITION_ACTION,
  APPLY_CHANGE_TITLE_ACTION,
  CHANGE_INFOGRAPHICS_EDITOR,
  CLEAN_REPORT_DATA_ACTION,
  CLEAR_VIEW_DATA,
  REFRESH_SECTIONS_DATA_SOURCE_ACTION,
  RESET_DATA_NOTES_ACTION,
  UPDATE_DATA_NOTES_ACTION,
  UPDATE_NARRATIVE_IN_STORAGE,
  UPDATE_REVIEW_STATE_DATA_ACTION,
  UPDATE_USER_SETTINGS_ACTION,
  UPDATE_USER_SETTINGS_FAILED,
  UPDATE_USER_SETTINGS_SUCCESS,
} from 'src/redux/constants';
import * as infographics from 'src/redux/infographics/reducer';
import {
  CANCEL_MULTINARRATIVE_ACTION_SUCCESS,
  LOCK_MULTINARRATIVE_ACTION_SUCCESS,
  RECEIVE_EXTRA_NARRATIVE_FROM_API,
  RECEIVE_MULTI_NARRATIVE_FROM_API,
  RECEIVE_MULTI_NARRATIVE_SINGLE_FROM_API,
  RECEIVE_NARRATIVE_FROM_API,
  RESET_MULTINARRATIVE_ACTION,
  RESTORE_VERSION_MULTINARRATIVE_ACTION_SUCCESS,
  SAVE_MULTINARRATIVE_ACTION,
  SAVE_MULTINARRATIVE_ACTION_FAILED,
  SAVE_MULTINARRATIVE_ACTION_SUCCESS,
  SET_EXTRA_NARRATIVE_ACTION,
  SET_NARRATIVE_ACTION,
} from 'src/redux/narrative/actions';
import * as narrative from 'src/redux/narrative/reducer';
import * as infoBox from 'src/redux/review/reducer';
import { RECEIVE_DATA_NOTES_FROM_API } from 'src/redux/sections/actions';
import * as dataNotes from 'src/redux/sections/reducer';
import {
  SET_SUBTITLE_ACTION,
  SET_SUBTITLE_SUCCESS_ACTION,
} from 'src/redux/subtitle/actions';
import * as subtitle from 'src/redux/subtitle/reducer';
import * as userSettings from 'src/redux/userSettings/reducer';
import type { IViewDataInitialState } from 'src/redux/viewData/types';
import * as api from './apiLifecycle';
import { resolveStorageKey } from './helpers';

export const viewData = (
  state: IViewDataInitialState = {},
  incomingAction: BaseAction,
): IViewDataInitialState => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- cross-slice actions are loosely typed at dispatch; handlers re-type their own payload
  const action = incomingAction as any;
  const key = resolveStorageKey(action) as string;

  switch (action.type) {
    case REQUEST_API_HOLDING_DATA:
      return api.requestApiHoldingData(state, key);
    case REQUEST_API:
    case REQUEST_API_LATEST:
      return api.requestApi(state, key);
    case RECEIVE_FROM_API:
      return api.receiveFromApi(state, action, key);
    case RECEIVE_FROM_API_HOLDING_DATA:
      return api.receiveFromApiHoldingData(state, action, key);
    case REQUEST_API_FAILURE:
      return api.requestApiFailure(state, action, key);
    case REQUEST_API_FAILURE_HOLDING_DATA:
      return api.requestApiFailureHoldingData(state, action, key);
    case CLEAR_API_KEYS:
      return api.clearApiKeys(state, action);
    case CLEAR_VIEW_DATA:
      return api.clearViewData();
    case CLEAN_REPORT_DATA_ACTION:
      return api.cleanReportData(state);
    case RESET_REPORTS_LIST:
      return api.resetReportsList(state);

    case REFRESH_SECTIONS_DATA_SOURCE_ACTION:
    case ACR_INFO_BOX_ACTION:
    case UPDATE_REVIEW_STATE_DATA_ACTION:
    case APPLY_CHANGE_TITLE_ACTION:
    case UPDATE_DATA_NOTES_ACTION:
    case ACR_REPORT_STATE_TRANSITION_ACTION:
    case UPDATE_USER_SETTINGS_ACTION:
      return api.setFetchingOnDataKey(state, action.dataKey);

    case UPDATE_NARRATIVE_IN_STORAGE:
      return narrative.updateNarrativeInStorage(state, action);
    case RECEIVE_NARRATIVE_FROM_API:
      return narrative.receiveNarrative(state, action);
    case RECEIVE_MULTI_NARRATIVE_FROM_API:
      return narrative.receiveMultiNarrative(state, action);
    case RECEIVE_MULTI_NARRATIVE_SINGLE_FROM_API:
      return narrative.receiveMultiNarrativeSingle(state, action);
    case RECEIVE_EXTRA_NARRATIVE_FROM_API:
      return narrative.receiveExtraNarrative(state, action);
    case LOCK_MULTINARRATIVE_ACTION_SUCCESS:
      return narrative.lockMultiNarrativeSuccess(state, action);
    case CANCEL_MULTINARRATIVE_ACTION_SUCCESS:
      return narrative.cancelMultiNarrativeSuccess(state, action);
    case SAVE_MULTINARRATIVE_ACTION:
      return narrative.saveMultiNarrative(state, action);
    case SAVE_MULTINARRATIVE_ACTION_FAILED:
      return narrative.saveMultiNarrativeFailed(state);
    case SAVE_MULTINARRATIVE_ACTION_SUCCESS:
      return narrative.saveMultiNarrativeSuccess(state, action);
    case RESET_MULTINARRATIVE_ACTION:
      return narrative.resetMultiNarrative(state, action);
    case RESTORE_VERSION_MULTINARRATIVE_ACTION_SUCCESS:
      return narrative.restoreVersionMultiNarrativeSuccess(state, action);
    case SET_NARRATIVE_ACTION:
      return narrative.setNarrative(state, action);
    case SET_EXTRA_NARRATIVE_ACTION:
      return narrative.setExtraNarrative(state, action);

    case SET_SUBTITLE_ACTION:
      return subtitle.setSubtitle(state, action);
    case SET_SUBTITLE_SUCCESS_ACTION:
      return subtitle.setSubtitleSuccess(state, action);

    case RECEIVE_INFOGRAPHICS_FROM_API:
      return infographics.receiveInfographics(state, action);
    case CHANGE_INFOGRAPHICS_EDITOR:
      return infographics.changeInfographicsEditor(state, action);

    case RESET_DATA_NOTES_ACTION:
      return dataNotes.resetDataNotes(state);
    case RECEIVE_DATA_NOTES_FROM_API:
      return dataNotes.receiveDataNotes(state, action);

    case UPDATE_USER_SETTINGS_SUCCESS:
      return userSettings.updateUserSettingsSuccess(state, action);
    case UPDATE_USER_SETTINGS_FAILED:
      return userSettings.updateUserSettingsFailed(state, action);

    case COMMENT_REQUEST_API:
    case COMMENT_REPLY_REQUEST_API:
      return comments.commentRequest(state);

    case REQUEST_POST_ACR_INFO_BOX_SUCCESS:
      return infoBox.receivePostAcrInfoBoxSuccess(state, action);

    default:
      return state;
  }
};
